import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../../api';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const ShiftWiseBarChart = ({ filters }) => {
  const [focusMode, setFocusMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiData, setApiData] = useState([]);
  const { accessToken } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const baseUrl = getRoundRobinBaseUrl();

        const filteredParams = {};
        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) {
            filteredParams[key] = filters[key];
          }
        });

        const response = await api.get(`${baseUrl}/superadminDashboard/getSuperAdminShiftWiseBiometricLeakage`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: filteredParams,
        });

        if (response.data?.success && response.data?.data) {
          const parsed = response.data.data.map((item) => ({
            shift: item.shift,
            leakage: parseFloat(item.leakagePercentage.replace('%', '')) || 0,
            leave: parseFloat(item.LeavePercentage.replace('%', '')) || 0,
          }));
          setApiData(parsed);
        } else {
          setApiData([]);
        }
      } catch (error) {
        console.error('Error fetching shift-wise leakage:', error);
        setApiData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (filters) {
      fetchData();
    }
  }, [accessToken, filters]);

  const handleFocusMode = () => {
    setFocusMode(true);
  };

  const processedData = apiData || [];
  const hasValidData = processedData.some((item) => item.leakage > 0 || item.leave > 0);

  const CardWrapper = ({ children, className = '' }) => (
    <div
      className={`relative w-full max-w-6xl mx-auto p-6 rounded-[2rem] backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] bg-white/70 border border-gray-200 overflow-hidden ${className}`}
    >
      <div className="absolute -top-20 -left-20 w-72 h-72 bg-[#1A2B5C]/20 rounded-full blur-[100px] z-0" />
      <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-[#EE1D23]/20 rounded-full blur-[100px] z-0" />
      <div className="relative z-10">{children}</div>
    </div>
  );

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center text-center text-gray-400">
      <svg className="w-24 h-24 mb-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 48 48">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M15 17h18M15 23h18M15 29h18M9 11h30M4 41h40M4 35h40M8 11v30M40 11v30"
        />
      </svg>
      <p className="text-sm font-semibold">No data available</p>
      <p className="text-xs">Please check back later</p>
    </div>
  );

  const LoadingDots = () => (
    <div className="flex space-x-2">
      <div className="w-4 h-4 bg-[#1A2B5C] rounded-full animate-bounce"></div>
      <div className="w-4 h-4 bg-[#EE1D23] rounded-full animate-bounce [animation-delay:0.2s]"></div>
      <div className="w-4 h-4 bg-[#1A2B5C] rounded-full animate-bounce [animation-delay:0.4s]"></div>
    </div>
  );

  return (
    <>
      <style>
        {`
          @keyframes slideZoomUp {
            0% { transform: translateY(60%) scale(0.8); opacity: 0; }
            100% { transform: translateY(0) scale(1); opacity: 1; }
          }
          .animate-slideZoomUp {
            animation: slideZoomUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          }
        `}
      </style>

      {/* Normal View */}
      {!focusMode && (
        <CardWrapper>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-bold text-[#1A2B5C] uppercase tracking-wide">Shift-wise Biometric Leakage</h3>
              <p className="text-sm text-gray-500 mb-4">Shift Performance Overview</p>
            </div>
            <button
              onClick={handleFocusMode}
              className="px-4 py-2 text-xs font-semibold rounded-full border border-[#1A2B5C] text-[#1A2B5C] hover:bg-[#1A2B5C] hover:text-white transition"
            >
              Focus Mode
            </button>
          </div>

          <div className="h-[22rem] flex items-center justify-center mt-4">
            {isLoading ? (
              <LoadingDots />
            ) : hasValidData ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={processedData} margin={{ bottom: 40 }}>
                  <XAxis dataKey="shift" />
                  <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tickFormatter={(tick) => `${tick}%`} />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Legend />
                  <Bar dataKey="leave" fill="#EE1D23" barSize={20} radius={[8, 8, 0, 0]} name="Leave">
                    {processedData.map((entry, index) => (
                      <Cell key={`leave-${index}`} fill={entry.leave === 0 ? 'transparent' : '#EE1D23'} />
                    ))}
                  </Bar>
                  <Bar dataKey="leakage" fill="#1A2B5C" barSize={20} radius={[8, 8, 0, 0]} name="Leakage">
                    {processedData.map((entry, index) => (
                      <Cell key={`leakage-${index}`} fill={entry.leakage === 0 ? 'transparent' : '#1A2B5C'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState />
            )}
          </div>
        </CardWrapper>
      )}

      {/* Focus Mode */}
      {focusMode && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-6xl mx-auto animate-slideZoomUp">
            <CardWrapper>
              <button
                onClick={() => setFocusMode(false)}
                className="absolute top-5 right-5 bg-white shadow-md rounded-full p-2 text-gray-600 hover:text-[#EE1D23] transition-transform hover:scale-110"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-2xl font-bold text-[#1A2B5C]">Shift-wise Biometric Leakage</h2>
                  <p className="text-sm text-gray-500 mb-4">Focus View</p>
                </div>
                <div className="h-2 w-32 rounded-full bg-gradient-to-r from-[#1A2B5C] to-[#EE1D23]" />
              </div>

              <div className="h-[65vh] flex items-center justify-center">
                {isLoading ? (
                  <LoadingDots />
                ) : hasValidData ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={processedData} margin={{ bottom: 40 }}>
                      <XAxis dataKey="shift" />
                      <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tickFormatter={(tick) => `${tick}%`} />
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Legend />
                      <Bar dataKey="leave" fill="#EE1D23" barSize={20} radius={[8, 8, 0, 0]} name="Leave">
                        {processedData.map((entry, index) => (
                          <Cell key={`focus-leave-${index}`} fill={entry.leave === 0 ? 'transparent' : '#EE1D23'} />
                        ))}
                      </Bar>
                      <Bar dataKey="leakage" fill="#1A2B5C" barSize={20} radius={[8, 8, 0, 0]} name="Leakage">
                        {processedData.map((entry, index) => (
                          <Cell key={`focus-leakage-${index}`} fill={entry.leakage === 0 ? 'transparent' : '#1A2B5C'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState />
                )}
              </div>
            </CardWrapper>
          </div>
        </div>
      )}
    </>
  );
};

export default ShiftWiseBarChart;
